/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable newline-before-return */
/* eslint-disable no-console */
/* eslint-disable max-len */
import { toast } from 'react-toastify';
import {
	createDocumentCollection,
	updateDocumentCollection,
	deleteDocumentCollection,
	CreateDocumentPayload,
	UpdateDocumentPayload
} from '../../../../../../../../containers/actions/document_management/document_management';
import { newNewsFeedUploadFiles } from '../../../../../../../../libs/HelperContent';
import { DocumentItemWithResponse } from './types';

// Constants
export const SUPPORTED_DOCUMENT_FORMATS = [ 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt' ] as const;
const MAX_DATA_URI_SIZE = 10 * 1024 * 1024; // 10MB
const DEFAULT_PAGE_SIZE_ESTIMATES = {
	pdf  : 30000,
	doc  : 50000,
	docx : 50000,
	ppt  : 40000,
	pptx : 40000,
	xls  : 45000,
	xlsx : 45000
} as const;

// MIME type to extension mapping
const MIME_TO_EXTENSION_MAP = {
	'application/pdf'                                                           : 'pdf',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document'   : 'docx',
	'application/msword'                                                        : 'doc',
	'application/x-msword'                                                      : 'doc',
	'application/vnd.openxmlformats-officedocument.presentationml.presentation' : 'pptx',
	'application/vnd.ms-powerpoint'                                             : 'ppt',
	'application/mspowerpoint'                                                  : 'ppt',
	'application/powerpoint'                                                    : 'ppt',
	'application/x-mspowerpoint'                                                : 'ppt',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'         : 'xlsx',
	'application/vnd.ms-excel'                                                  : 'xls',
	'application/x-excel'                                                       : 'xls',
	'application/x-msexcel'                                                     : 'xls',
	'text/plain'                                                                : 'txt'
} as const;

// Types
type SupportedFormat = typeof SUPPORTED_DOCUMENT_FORMATS[number];
type SourceType = 'system' | 'media_drive' | 'user' | 'external' | 'embed_link';
type DocumentStatus = 'draft' | 'published' | 'scheduled' | 'unpublished';
// type VisibilityType = 'public' | 'private';

// For UpdateDocumentPayload compatibility
type UpdateSourceType = 'system' | 'media_drive' | 'user' | 'external';

interface AccessControl {
	internalEmployees?: {
		allSelected?: boolean;
		departments?: Array<{ name: string; count?: number }>;
		specificUsers?: Array<{ userId: string; name: string; email: string; role?: string }>;
	};
	userGroups?: Array<{ type: string; count?: number }>;
	participantCount?: number;
	participantLimit?: number;
}

interface DocumentMetadata {
	author?: string;
	createdDate?: string;
	modifiedDate?: string;
	pageCount?: number;
	fileType?: string;
	isDataUri?: boolean;
	sizeInBytes?: number;
}

interface DocumentFile {
	title: string;
	description?: string;
	url: string;
	fileType: string;
	fileSize: number;
	pageCount?: number;
	metadata?: DocumentMetadata;
	coverImage?: string;
	categoryIds: string[];
	tags: string[];
	// visibility: VisibilityType;
	status: DocumentStatus;
	allowDownload: boolean;
	allowShare: boolean;
	order: number;
	uploadedAt: string;
	// accessControl: AccessControl;
	platform?: string;
}

interface CreateDocPayload {
	companyId: string;
	sourceType: SourceType;
	files: DocumentFile[];
}

interface DocumentUpdateOptions {
	sourceType?: SourceType;
	files: any;
	documentId: string;
	title?: string;
	description?: string;
	// visibility?: VisibilityType;
	categoryIds?: string[];
	tags?: string[];
	url?: string;
	fileType?: string;
	fileSize?: number;
	thumbnailUrl?: string;
	allowDownload?: boolean;
	allowShare?: boolean;
	status?: DocumentStatus;
	// accessControl?: AccessControl;
}

interface ExtendedUpdateDocumentPayload extends UpdateDocumentPayload {
	sourceType?: UpdateSourceType;
	fileIndex?: number;
	files?: Array<Partial<DocumentFile>>;
	// visibility?: VisibilityType;
	categoryIds?: string[];
	tags?: string[];
	allowDownload?: boolean;
	allowShare?: boolean;
	status?: DocumentStatus;
	// accessControl?: AccessControl;
}

export interface DocumentUrlData {
	url: string;
	title?: string;
	description?: string;
	fileType: string;
	coverImage?: string;
	platform?: string;
	categoryIds?: string[];
	tags?: string[];
	// visibility?: VisibilityType;
	status?: DocumentStatus;
	allowDownload?: boolean;
	allowShare?: boolean;
	// accessControl?: AccessControl;
	metadata?: {
		isDataUri: boolean;
		sizeInBytes?: number;
		author?: string;
		createdDate?: string;
		modifiedDate?: string;
		pageCount?: number;
	};
}

interface DocumentUploadOptions {
	title?: string;
	description?: string;
	categoryIds?: string[];
	tags?: string[];
	// visibility?: VisibilityType;
	status?: DocumentStatus;
	fileType?: string;
	coverImage?: string;
	platform?: string;
	allowDownload?: boolean;
	allowShare?: boolean;
	// accessControl?: AccessControl;
	metadata?: {
		isDataUri?: boolean;
		sizeInBytes?: number;
		pageCount?: number;
		author?: string;
		createdDate?: string;
		modifiedDate?: string;
	};
	requestFrom?: string;
	companyId?: string;
}

// Utility Functions
const getCurrentISODate = (): string => new Date().toISOString();

/**
 * Check if a string is a valid URL
 */
const isValidUrl = (url: string): boolean =>
{
	try
	{
		const urlObj = new URL(url);

		return Boolean(urlObj);
	}
	catch
	{
		return false;
	}
};

const isDataUri = (url: string): boolean => url.startsWith('data:');

/**
 * Estimates page count based on file size and type
 */
const estimatePageCount = (fileSize: number, fileType: string): number =>
{
	const estimate = DEFAULT_PAGE_SIZE_ESTIMATES[fileType as keyof typeof DEFAULT_PAGE_SIZE_ESTIMATES];

	return estimate ? Math.floor(fileSize / estimate) + 1 : 1;
};

/**
 * Extract document metadata from a File object with improved error handling
 */
export const getDocumentMetadata = async (file: File): Promise<DocumentMetadata> =>
{
	try
	{
		const fileExtension = file.name.split('.').pop()
			?.toLowerCase() as SupportedFormat;
		const currentDate = getCurrentISODate();

		const metadata: DocumentMetadata = {
			createdDate  : currentDate,
			modifiedDate : currentDate,
			fileType     : fileExtension
		};

		if (fileExtension && DEFAULT_PAGE_SIZE_ESTIMATES[fileExtension])
		{
			metadata.pageCount = estimatePageCount(file.size, fileExtension);
		}

		return metadata;
	}
	catch (error)
	{
		console.error('Error getting document metadata:', error);
		return {
			createdDate  : getCurrentISODate(),
			modifiedDate : getCurrentISODate()
		};
	}
};

/**
 * Optimized file type extraction with better URL parsing
 */
export const extractFileTypeFromUrl = (url: string): string | null =>
{
	if (!url || !isValidUrl(url)) return null;

	try
	{
		// Direct extension match (most common case)
		const extensionMatch = url.match(/\.([a-zA-Z0-9]{2,4})(?:\?|#|$)/);

		if (extensionMatch)
		{
			const extension = extensionMatch[1].toLowerCase();

			return SUPPORTED_DOCUMENT_FORMATS.includes(extension as SupportedFormat) ? extension : null;
		}

		// Parse URL for query parameters
		const urlObj = new URL(url);
		const format = urlObj.searchParams.get('format') || urlObj.searchParams.get('type');

		if (format)
		{
			const lowerFormat = format.toLowerCase();

			return SUPPORTED_DOCUMENT_FORMATS.includes(lowerFormat as SupportedFormat) ? lowerFormat : null;
		}

		return null;
	}
	catch (error)
	{
		console.error('Error extracting file type from URL:', error);
		return null;
	}
};

/**
 * Optimized data URI file type extraction
 */
export const extractFileTypeFromDataUri = (dataUri: string): string | null =>
{
	if (!isDataUri(dataUri)) return null;

	const mimeMatch = dataUri.match(/^data:([^;]+);/);

	if (!mimeMatch) return null;

	const mimeType = mimeMatch[1].toLowerCase() as keyof typeof MIME_TO_EXTENSION_MAP;

	return MIME_TO_EXTENSION_MAP[mimeType] || mimeType.split('/')[1] || null;
};

/**
 * More accurate data URI size estimation
 */
export const estimateDataUriSize = (dataUri: string): number =>
{
	if (!dataUri || typeof dataUri !== 'string') return 0;

	const commaIndex = dataUri.indexOf(',');

	if (commaIndex === -1) return 0;

	const base64Data = dataUri.substring(commaIndex + 1);
	// Account for padding in base64
	const paddingCount = (base64Data.match(/[=]/g) || []).length;

	return Math.floor((base64Data.length * 3) / 4) - paddingCount;
};

/**
 * Comprehensive data URI validation
 */
export const validateDocumentDataUri = (dataUri: string): { valid: boolean; reason?: string } =>
{
	if (!isDataUri(dataUri))
	{
		return { valid: false, reason: 'Not a valid data URI' };
	}

	const size = estimateDataUriSize(dataUri);

	if (size > MAX_DATA_URI_SIZE)
	{
		const sizeMB = (size / (1024 * 1024)).toFixed(2);

		return {
			valid  : false,
			reason : `Document too large (${sizeMB} MB). Maximum size is 10MB.`
		};
	}

	const fileType = extractFileTypeFromDataUri(dataUri);

	if (!fileType || !SUPPORTED_DOCUMENT_FORMATS.includes(fileType as SupportedFormat))
	{
		return {
			valid  : false,
			reason : 'Unsupported document format. Please use PDF, DOCX, or other supported formats.'
		};
	}

	return { valid: true };
};

/**
 * Create default access control object
 */
const createDefaultAccessControl = (): AccessControl => ({
	internalEmployees : {
		allSelected   : true,
		departments   : [],
		specificUsers : []
	},
	userGroups       : [],
	participantCount : 0,
	participantLimit : 100
});

/**
 * Validate and convert file type to ensure backend compatibility
 */
export const validateAndNormalizeFileType = (
	mimeType: string,
	fileName: string
): { fileType: string; isValid: boolean; source: 'mime' | 'filename' | 'fallback' } =>
{
	// Try MIME type mapping first
	const normalizedMimeType = mimeType.toLowerCase() as keyof typeof MIME_TO_EXTENSION_MAP;
	const fileType = MIME_TO_EXTENSION_MAP[normalizedMimeType];

	if (fileType && SUPPORTED_DOCUMENT_FORMATS.includes(fileType as SupportedFormat))
	{
		return { fileType, isValid: true, source: 'mime' };
	}

	// Try filename extension
	const extensionFromName = fileName.split('.').pop()
		?.toLowerCase();

	if (extensionFromName && SUPPORTED_DOCUMENT_FORMATS.includes(extensionFromName as SupportedFormat))
	{
		return { fileType: extensionFromName, isValid: true, source: 'filename' };
	}

	// Fallback to MIME type parsing (for backward compatibility)
	const fallbackType = mimeType.split('/')[1];
	const isValidFallback = SUPPORTED_DOCUMENT_FORMATS.includes(fallbackType as SupportedFormat);

	return {
		fileType : fallbackType,
		isValid  : isValidFallback,
		source   : 'fallback'
	};
};

/**
 * Process file upload and return standardized file data
 */
const processFileUpload = async (file: File, options: DocumentUploadOptions): Promise<{
	url: string;
	fileType: string;
	fileSize: number;
	title: string;
	metadata: DocumentMetadata;
}> =>
{
	const uploadResult = await newNewsFeedUploadFiles([ file ], `${options.companyId}/resource`);
	const { name, url, type, size } = uploadResult[0];

	// Validate and normalize file type for backend compatibility
	const { fileType, isValid, source } = validateAndNormalizeFileType(type, file.name);

	// Log for debugging (can be removed in production)
	console.log('File type validation:', {
		originalMimeType   : type,
		fileName           : file.name,
		normalizedFileType : fileType,
		isValid,
		detectionSource    : source
	});

	// Throw error if file type is not supported
	if (!isValid)
	{
		throw new Error(`Unsupported file type: ${type}. Supported formats: ${SUPPORTED_DOCUMENT_FORMATS.join(', ')}`);
	}

	return {
		url,
		fileType,
		fileSize : size,
		title    : options.title || name || file.name.replace(/\.[^/.]+$/, ''),
		metadata : await getDocumentMetadata(file)
	};
};

/**
 * Process URL upload and return standardized file data
 */
const processUrlUpload = (url: string, options: DocumentUploadOptions): {
	url: string;
	fileType: string;
	fileSize: number;
	title: string;
	metadata: DocumentMetadata;
} =>
{
	const isDataUriUrl = isDataUri(url);
	const currentDate = getCurrentISODate();

	if (isDataUriUrl && !options.metadata?.isDataUri)
	{
		const validation = validateDocumentDataUri(url);

		if (!validation.valid)
		{
			throw new Error(validation.reason);
		}
	}

	const fileType = isDataUriUrl
		? extractFileTypeFromDataUri(url) || options.fileType || 'pdf'
		: extractFileTypeFromUrl(url) || options.fileType || 'pdf';

	const fileSize = isDataUriUrl
		? options.metadata?.sizeInBytes || estimateDataUriSize(url)
		: 0;

	const metadata: DocumentMetadata = {
		author       : 'URL Upload',
		createdDate  : options.metadata?.createdDate || currentDate,
		modifiedDate : options.metadata?.modifiedDate || currentDate,
		fileType,
		...(isDataUriUrl && {
			isDataUri   : true,
			sizeInBytes : fileSize
		}),
		pageCount : options.metadata?.pageCount || estimatePageCount(fileSize || 1000000, fileType)
	};

	return {
		url,
		fileType,
		fileSize,
		title : options.title || `Document-${Date.now()}`,
		metadata
	};
};

/**
 * Build document file payload
 */
const buildDocumentFilePayload = (
	fileData: { url: string; fileType: string; fileSize: number; title: string; metadata: DocumentMetadata },
	options: DocumentUploadOptions
): DocumentFile =>
{
	const currentDate = getCurrentISODate();

	return {
		title         : fileData.title,
		description   : options.description || '',
		url           : fileData.url,
		fileType      : fileData.fileType,
		fileSize      : fileData.fileSize,
		metadata      : fileData.metadata,
		coverImage    : options.coverImage,
		categoryIds   : options.categoryIds || [],
		tags          : options.tags || [],
		// visibility    : options.visibility || 'private',
		status        : options.status || 'draft',
		allowDownload : options.allowDownload ?? true,
		allowShare    : options.allowShare ?? true,
		order         : 0,
		uploadedAt    : currentDate,
		// accessControl : options.accessControl || createDefaultAccessControl(),
		...(options.platform && {
			platform : options.platform || (isDataUri(fileData.url) ? 'data_uri_document' : 'document_url')
		})
	};
};

/**
 * Main document upload handler with improved error handling and performance
 */
export const handleDocumentUpload = async (
	fileOrUrl: File | string,
	companyId: string,
	source: string,
	options: DocumentUploadOptions = {}
): Promise<any> =>
{
	try
	{
		const isFile = fileOrUrl instanceof File;

		// Validate URL if not a file and not a data URI
		if (!isFile && !isDataUri(fileOrUrl as string))
		{
			const fileExt = extractFileTypeFromUrl(fileOrUrl as string);

			if (!fileExt || !SUPPORTED_DOCUMENT_FORMATS.includes(fileExt as SupportedFormat))
			{
				throw new Error('URL must point to a document with a supported extension (.pdf, .docx, etc.)');
			}
		}

		// Process file or URL
		const fileData = isFile
			? await processFileUpload(fileOrUrl as File, { ...options, companyId })
			: processUrlUpload(fileOrUrl as string, options);

		// Build payload
		const filePayload = buildDocumentFilePayload(fileData, options);

		// Determine source type - convert embed_link to system
		const sourceType = (options.requestFrom === 'embedLink' || source === 'embed_link')
			? 'system'
			: source as SourceType;

		const payload: CreateDocPayload = {
			companyId,
			sourceType,
			files : [ filePayload ]
		};

		console.log('Sending payload to API:', { companyId, sourceType, fileCount: payload.files.length });

		// API call
		return await createDocumentCollection(payload as any);
	}
	catch (error)
	{
		console.error('Error in document upload process:', error);
		toast.error('Failed to upload document. Please check the file type and try again.');
		throw error;
	}
};

/**
 * Silent document update helper
 */
const updateDocumentSilent = async (
	documentId: string,
	options: DocumentUpdateOptions
): Promise<any> =>
{
	// Handle sourceType - convert embed_link to system
	const sourceType: UpdateSourceType = options.sourceType === 'embed_link'
		? 'system'
		: (options.sourceType as UpdateSourceType) || 'system';

	const updatePayload: ExtendedUpdateDocumentPayload = {
		sourceType
	};

	if (options?.files?.length > 0)
	{
		updatePayload.files = options.files.map((file: any) =>
		{
			const fileDetails: any = {};

			// Use object destructuring and conditional assignment for cleaner code
			const fieldsToMap = [
				'title', 'description', 'url', 'fileType', 'fileSize',
				'dimensions', 'categoryIds', 'tags', 'status',
				'allowDownload', 'allowShare'
			];

			fieldsToMap.forEach((field) =>
			{
				if (file[field] !== undefined && file[field] !== null && file[field] !== '')
				{
					fileDetails[field] = file[field];
				}
			});

			if (file.order !== undefined) fileDetails.order = file.order;

			if (file.thumbnail)
			{
				fileDetails.thumbnail = {
					url       : file.thumbnail.url,
					canEdit   : file.thumbnail.canEdit ?? true,
					canDelete : file.thumbnail.canDelete ?? false
				};
			}

			return fileDetails;
		});
	}

	return await updateDocumentCollection(documentId, updatePayload);
};

/**
 * Optimized batch document update with better error handling
 */
export const handleDocumentUpdate = async (
	companyId: string,
	documents: any[],
	updatedData: any
): Promise<any[]> =>
{
	if (!documents?.length)
	{
		throw new Error('No document IDs provided for update');
	}

	try
	{
		console.log('Documents:', documents);
		const updatePromises = documents.map((documentItem) =>
		{
			const isStringId = typeof documentItem === 'string';
			const existingData: any = isStringId ? {} : documentItem;
			const documentId = isStringId ? documentItem : existingData.id;

			// Optimize array merging
			const mergeArrays = (existing: any[] = [], updated: any[] = []) =>
				(documents.length > 1 ? Array.from(new Set([ ...existing, ...updated ])) : updated);

			const options: any = {
				documentId,
				files : [ {
					title         : updatedData.title ?? (documents.length > 1 ? existingData.title : undefined),
					description   : updatedData.description ?? (documents.length > 1 ? existingData.description : undefined),
					url           : updatedData.url ?? (documents.length > 1 ? existingData.url : undefined),
					// categoryIds   : updatedData.categoryIds !== undefined ? updatedData.categoryIds : undefined,
					tags          : mergeArrays(existingData.tags, updatedData.tags),
					// visibility    : updatedData.visibility ?? (documents.length > 1 ? existingData.visibility : undefined),
					allowDownload : updatedData.allowDownload ?? (documents.length > 1 ? existingData.allowDownload : undefined),
					allowShare    : updatedData.allowShare ?? (documents.length > 1 ? existingData.allowShare : undefined),
					// accessControl : updatedData.accessControl ?? (documents.length > 1 ? existingData.accessControl : undefined),
					status        : updatedData.status ?? (documents.length > 1 ? existingData.status : undefined)
				} ],
				sourceType : 'system'
			};

			if (updatedData?.categoryIds?.length > 0 && updatedData?.categoryIds?.[0] !== existingData?.categoryIds?.[0])
			{
				options.files[0].categoryIds = updatedData.categoryIds;
			}

			// Clean undefined values
			Object.keys(options.files[0]).forEach((key) =>
			{
				if (options.files[0][key] === undefined && options.files[0][key] !== null && options.files[0][key] !== '')
				{
					delete options.files[0][key];
				}
			});

			return updateDocumentSilent(documentId, options);
		});

		const responses = await Promise.all(updatePromises);

		const refreshEvent = new CustomEvent('contentStatusChanged', {
			detail : {
				contentId   : documents.map((document) => document.id),
				contentType : 'Documents',
				status      : 'updated'
			}
		});

		window.dispatchEvent(refreshEvent);

		return responses;
	}
	catch (error)
	{
		console.error('Error updating documents:', error);
		throw error;
	}
};

/**
 * Optimized document deletion
 */
export const handleDocumentDelete = async (documentId: string): Promise<void> =>
{
	try
	{
		const collectionId = documentId.split('_')[0];

		await deleteDocumentCollection(collectionId);
	}
	catch (error)
	{
		console.error('Error deleting document:', error);
		toast.error('Failed to delete document. Please try again.');
		throw error;
	}
};

/**
 * Optimized URL document upload with better error handling
 * This also prevents duplicate API calls by setting a requestTimestamp in localStorage
 */
export const uploadDocumentURL = async (
	companyId: string,
	documentData: DocumentUrlData
): Promise<any> =>
{
	// Create a unique key for the current request to prevent duplicate uploads
	const requestKey = `doc_upload_${companyId}_${documentData.url}`;
	const currentTime = Date.now();
	const lastUploadTime = localStorage.getItem(requestKey);

	// If the same document URL was uploaded within the last 2 seconds, skip this upload
	if (lastUploadTime && (currentTime - parseInt(lastUploadTime)) < 2000)
	{
		return { status: 'success', message: 'Document already being processed', skipDuplicate: true };
	}

	// Store the current timestamp for this request
	localStorage.setItem(requestKey, currentTime.toString());

	const toastId = toast.info('Processing document URL...', { autoClose: false });

	try
	{
		const response = await handleDocumentUpload(
			documentData.url,
			companyId,
			'system',
			{
				title         : documentData.title,
				description   : documentData.description,
				fileType      : documentData.fileType,
				coverImage    : documentData.coverImage,
				platform      : documentData.platform,
				categoryIds   : documentData.categoryIds,
				tags          : documentData.tags,
				// visibility    : documentData.visibility,
				status        : documentData.status,
				allowDownload : documentData.allowDownload,
				allowShare    : documentData.allowShare,
				// accessControl : documentData.accessControl,
				metadata      : documentData.metadata,
				requestFrom   : 'embedLink'
			}
		);

		toast.update(toastId, {
			render    : 'Document URL processed successfully!',
			type      : toast.TYPE.SUCCESS,
			autoClose : 3000
		});

		return response;
	}
	catch (error)
	{
		console.error('Error processing document URL:', error);

		let errorMessage = 'Failed to process document URL. Please try again.';

		if (error instanceof Error)
		{
			if (error.message.includes('network'))
			{
				errorMessage = 'Network error. Please check your connection and try again.';
			}
			else if (error.message.includes('timeout'))
			{
				errorMessage = 'Request timed out. Please try again.';
			}
			else if (error.message.includes('permission'))
			{
				errorMessage = 'Permission denied. You may not have access to add documents.';
			}
			else
			{
				errorMessage = error.message;
			}
		}

		toast.update(toastId, {
			render    : errorMessage,
			type      : toast.TYPE.ERROR,
			autoClose : 5000
		});

		// Clear the timestamp in case of error so a retry can work
		localStorage.removeItem(requestKey);

		throw error;
	}
};